import React, { createContext, useState, useEffect, useCallback } from 'react';
import authService from '../services/api/auth.service';
import localStorageService from '../services/storage/localStorage.service';

// Cria o contexto
export const AuthContext = createContext({});

/**
 * Provider de autenticação
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  /**
   * Verifica se usuário está autenticado ao carregar app
   */
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorageService.getAuthToken();
        const userData = localStorageService.getUserData();

        if (token && userData) {
          // Valida token
          const isValid = await authService.validateToken();
          
          if (isValid) {
            setUser(userData);
            setIsAuthenticated(true);
          } else {
            // Token inválido, limpa dados
            localStorageService.clearAuth();
          }
        }
      } catch (error) {
        console.error('Erro ao verificar autenticação:', error);
        localStorageService.clearAuth();
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  /**
   * Realiza login
   * @param {Object} credentials - { email, password, type }
   * @returns {Promise}
   */
  const login = useCallback(async (credentials) => {
    try {
      setLoading(true);
      
      // authService.login já retorna { token, user, expiresIn }
      const result = await authService.login(credentials);
      
      console.log('✅ Login bem-sucedido:', result);
      
      // Define user e isAuthenticated
      setUser(result.user);
      setIsAuthenticated(true);
      
      return { success: true, data: result };
    } catch (error) {
      console.error('❌ Erro no login (AuthContext):', error);
      return { 
        success: false, 
        error: error.message || 'Erro ao fazer login' 
      };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Realiza logout
   */
  const logout = useCallback(async () => {
    try {
      setLoading(true);
      await authService.logout();
    } catch (error) {
      console.error('Erro no logout:', error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      setLoading(false);
    }
  }, []);

  /**
   * Atualiza dados do usuário
   * @returns {Promise}
   */
  const refreshUser = useCallback(async () => {
    try {
      const userData = await authService.getCurrentUser();
      setUser(userData);
      return { success: true, data: userData };
    } catch (error) {
      console.error('Erro ao atualizar dados do usuário:', error);
      return { 
        success: false, 
        error: error.message || 'Erro ao atualizar dados' 
      };
    }
  }, []);

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    logout,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};